import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Crown, Pencil, Users, Server, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm] = useState({ plan: 'free', max_users: 3, max_hosts: 50, max_reports: 10 });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/super/plans`, { headers: getSuperAdminHeader() });
      setPlans(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openEdit = (t) => {
    setEditTenant(t);
    setForm({ plan: t.plan, max_users: t.max_users, max_hosts: t.max_hosts, max_reports: t.max_reports });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super/plans/${editTenant.tenant_id}`, form, { headers: getSuperAdminHeader() });
      toast.success('Plano atualizado');
      setEditTenant(null);
      fetchData();
    } catch (err) { toast.error('Erro ao atualizar'); }
    finally { setSaving(false); }
  };

  const planDefaults = { free: { max_users: 3, max_hosts: 50, max_reports: 10 }, pro: { max_users: 20, max_hosts: 500, max_reports: 100 }, enterprise: { max_users: -1, max_hosts: -1, max_reports: -1 } };

  const onPlanChange = (plan) => {
    const d = planDefaults[plan] || planDefaults.free;
    setForm({ plan, ...d });
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Planos e Limites</h2>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead className="w-28">Plano</TableHead>
                <TableHead className="w-28 text-center">Usuários</TableHead>
                <TableHead className="w-28 text-center">Hosts</TableHead>
                <TableHead className="w-28 text-center">Relatórios</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map(t => (
                <TableRow key={t.tenant_id}>
                  <TableCell className="font-medium">{t.company_name}</TableCell>
                  <TableCell><Badge style={{ backgroundColor: t.plan_color, color: '#fff' }}>{t.plan_label}</Badge></TableCell>
                  <TableCell className="text-center text-sm">
                    <span className={t.max_users > 0 && t.current_users >= t.max_users ? 'text-red-600 font-bold' : ''}>
                      {t.current_users}{t.max_users > 0 ? `/${t.max_users}` : '/∞'}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-sm">{t.max_hosts > 0 ? t.max_hosts : '∞'}</TableCell>
                  <TableCell className="text-center text-sm">{t.max_reports > 0 ? t.max_reports : '∞'}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editTenant} onOpenChange={(v) => { if (!v) setEditTenant(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Crown className="h-5 w-5" />{editTenant?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Plano</Label>
              <Select value={form.plan} onValueChange={onPlanChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><Users className="h-3 w-3" />Max Usuários</Label>
                <Input type="number" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: parseInt(e.target.value) || 0 })} />
                <p className="text-xs text-gray-400">-1 = ilimitado</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><Server className="h-3 w-3" />Max Hosts</Label>
                <Input type="number" value={form.max_hosts} onChange={(e) => setForm({ ...form, max_hosts: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><FileText className="h-3 w-3" />Max Relatórios</Label>
                <Input type="number" value={form.max_reports} onChange={(e) => setForm({ ...form, max_reports: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTenant(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
